import { base64Of, pcm16, routeSampleRate } from "./fayin-pcm.js";
import { pitchOf } from "./fayin-pitch.js";
import {
  type Playing,
  playAddress,
  type Recording,
  resampled,
  startRecording,
} from "./fayin-recorder.js";
import { trimmedVoice } from "./fayin-trim.js";

/**
 * A button held while a learner says one word, and the recording it gives.
 *
 * Shared by the speech card in 复习 (`./fuxi-speech.ts`) and the 语音答题
 * Exercise (`./lianxi-shuoci.ts`), which differ only in where the recording
 * goes: the card puts it in a form and submits it, and the Exercise posts it
 * to `/user/lianxi/`. Both are marked by `src/user/spoken-word-hearing.ts`.
 *
 * **It reuses the 发音 recorder** through its exports and changes none of it:
 * the microphone opened once and kept open, the trim to the voice, and the
 * samples as the route takes them. The button is the page's own, so 发音's
 * styling can move without moving this.
 *
 * **Recording is running before the learner is told to speak.** The status
 * says "Say the word now" only once the recorder has started, and a press let
 * go before it started keeps nothing.
 *
 * **An attempt with no voice in it is never sent.** A recording in which the
 * pitch tracker finds no voiced frame is answered "didn't catch that" here,
 * and costs the learner no attempt.
 */

/** The shortest press worth hearing, in seconds. */
const shortest = 0.3;

/** The longest a word is recorded for, in seconds, inside the route's five. */
const longest = 4.5;

/**
 * How long recording runs on after the button is let go, in milliseconds, so
 * the end of the last syllable is not cut off. 发音's figure.
 */
const releaseTailMs = 250;

/** What a button records and plays with. The browser's unless a test gives one. */
export interface SpeechAudio {
  readonly start: () => Promise<Recording>;
  readonly resample: (buffer: AudioBuffer) => Promise<Float32Array>;
  readonly play: (address: string) => Playing;
}

/** The microphone, Web Audio and an `<audio>` element. */
export const browserAudio: SpeechAudio = {
  start: startRecording,
  resample: resampled,
  play: playAddress,
};

/** A line the status says, in English, 简体 and 繁體. */
export type Said = readonly [string, string, string];

/** The lines the status says. */
export const speechSaid = {
  speak: [
    "Say the word now. Let go when you've finished.",
    "现在说出这个词。说完后松开。",
    "現在說出這個詞。說完後鬆開。",
  ],
  checking: ["Checking…", "检查中…", "檢查中…"],
  short: [
    "Hold the button while you speak, then let go.",
    "说话时按住按钮，说完松开。",
    "說話時按住按鈕，說完鬆開。",
  ],
  unclear: [
    "Didn't catch that. Try again or type your answer.",
    "没听清。再说一次，或者打字回答。",
    "沒聽清。再說一次，或者打字回答。",
  ],
  microphone: [
    "We can't use your microphone. Check that this site has permission to use it. You can also type your answer.",
    "无法使用麦克风。请检查是否已允许本站使用麦克风。也可以打字回答。",
    "無法使用麥克風。請檢查是否已允許本站使用麥克風。也可以打字回答。",
  ],
  failed: [
    "We couldn't read your recording. Try again or type your answer.",
    "无法读取录音。再试一次，或者打字回答。",
    "無法讀取錄音。再試一次，或者打字回答。",
  ],
  space: [
    "You can also hold the space bar to speak.",
    "也可以按住空格键说话。",
    "也可以按住空白鍵說話。",
  ],
} as const satisfies Record<string, Said>;

/** One half of a status line, as `src/user/bilingual.ts` draws it. */
function half(
  page: Document,
  text: string,
  lang?: { readonly lang: string; readonly writing: string },
): HTMLElement {
  const span = page.createElement("span");

  span.className = lang === undefined ? "cb-say-en" : "cb-say-zh";
  span.textContent = text;

  if (lang !== undefined) {
    span.lang = lang.lang;
    span.setAttribute("data-cb-writing", lang.writing);
  }

  return span;
}

/**
 * Put a line in a status, in its three writings, or empty it.
 * @param status - The status, or nothing where the page has none.
 * @param line - What to say, or nothing to say nothing.
 */
export function tellOn(
  status: HTMLElement | null,
  line: Said | undefined,
): void {
  if (status === null) {
    return;
  }

  const page = status.ownerDocument;

  status.replaceChildren(
    ...(line === undefined
      ? []
      : [
          half(page, line[0]),
          half(page, line[1], { lang: "zh-Hans", writing: "zh-CN" }),
          half(page, line[2], { lang: "zh-Hant", writing: "zh-TW zh-HK" }),
        ]),
  );
}

/** Whether any frame of a recording is voiced. */
function hasVoice(samples: Float32Array): boolean {
  return pitchOf(samples, routeSampleRate).some(
    (frame) => frame.hz !== undefined,
  );
}

/**
 * Whether a key held on the page is space held to speak through this button.
 *
 * **Space records wherever the caret is**, so a learner who pressed the hint
 * or the Listen first can still hold space to speak. The press is refused
 * where they are typing, where a modifier is held, and where the button is
 * disabled or inside something hidden, as it is once the Exercise has marked
 * a word and offers Next. A focused button pressed with space would act on
 * the key's release, which the caller's `preventDefault` stops.
 *
 * **A button swapped out of the page lets go of the page.** A 复习 card is
 * replaced by htmx on every answer, and its listeners are removed here the
 * first time a key reaches them after it has gone.
 */
function spaceRecords(
  button: HTMLButtonElement,
  event: KeyboardEvent,
  onKeyDown: (event: KeyboardEvent) => void,
  onKeyUp: (event: KeyboardEvent) => void,
): boolean {
  if (!button.isConnected) {
    button.ownerDocument.removeEventListener("keydown", onKeyDown);
    button.ownerDocument.removeEventListener("keyup", onKeyUp);

    return false;
  }

  return (
    event.key === " " &&
    !event.altKey &&
    !event.ctrlKey &&
    !event.metaKey &&
    !isTyping(event.target) &&
    !button.disabled &&
    button.closest("[hidden]") === null
  );
}

/**
 * Whether a key belongs to something being typed into, which keeps its own
 * space. The same test `./fuxi-keys.ts` makes.
 */
function isTyping(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

/** What a held button is bound with. */
export interface HoldToSpeakProperties {
  /** The button held while speaking. */
  readonly button: HTMLButtonElement;

  /** Where the status lines go. */
  readonly status: HTMLElement | null;

  /**
   * Where the attribute marking a recording under way goes, which the
   * stylesheet pulses the button by.
   */
  readonly recordingOn: Element;
  readonly audio: SpeechAudio;

  /**
   * Given the recording, cut to the voice, as base64 16-bit samples at 16 kHz,
   * the way `src/user/spoken-word-hearing.ts` reads them.
   */
  readonly onSpoken: (spoken: string) => void;

  /** Told that the microphone was refused or is missing. */
  readonly onMicrophoneRefused: () => void;
}

/**
 * Listen to a button held to speak, and show it.
 *
 * The button is drawn `hidden` and shown here, so a learner with no script is
 * offered only the keyboard.
 * @param properties - The button, its status, and what to do with what was said.
 */
export function holdToSpeak(properties: HoldToSpeakProperties): void {
  const { button, status, audio, recordingOn } = properties;
  let recording: Recording | undefined;
  let held = false;
  let busy = false;

  const tell = (line: Said | undefined): void => {
    tellOn(status, line);
  };

  const finish = async (): Promise<void> => {
    const stopping = recording;

    recording = undefined;
    recordingOn.removeAttribute("data-cb-fuxi-speech-recording");

    if (stopping === undefined) {
      return;
    }

    busy = true;

    try {
      const buffer = await stopping.stop();

      if (buffer.duration < shortest) {
        tell(speechSaid.short);
        return;
      }

      const samples = await audio.resample(buffer);

      if (!hasVoice(samples)) {
        tell(speechSaid.unclear);
        return;
      }

      properties.onSpoken(
        base64Of(pcm16(trimmedVoice(samples, routeSampleRate))),
      );
    } catch {
      tell(speechSaid.failed);
    } finally {
      busy = false;
    }
  };

  const begin = (): void => {
    if (busy || recording !== undefined) {
      return;
    }

    held = true;
    busy = true;
    tell(undefined);
    audio.start().then(
      (started) => {
        busy = false;

        if (!held) {
          // Let go before the recorder started, so nothing said is in it.
          started.stop().catch(() => undefined);
          return;
        }

        recording = started;
        recordingOn.setAttribute("data-cb-fuxi-speech-recording", "");
        tell(speechSaid.speak);
        setTimeout(() => {
          if (recording === started) {
            void finish();
          }
        }, longest * 1000);
      },
      () => {
        busy = false;
        held = false;
        tell(speechSaid.microphone);
        properties.onMicrophoneRefused();
      },
    );
  };

  const release = (): void => {
    held = false;

    if (recording !== undefined) {
      const stopping = recording;

      setTimeout(() => {
        if (recording === stopping) {
          void finish();
        }
      }, releaseTailMs);
    }
  };

  button.addEventListener("pointerdown", (event) => {
    if (event.button !== 0 || button.disabled) {
      return;
    }

    // Holding on a phone would otherwise select text or open a menu.
    event.preventDefault();

    try {
      button.setPointerCapture(event.pointerId);
    } catch {
      // Recorded all the same.
    }

    begin();
  });

  for (const name of ["pointerup", "pointercancel"]) {
    button.addEventListener(name, release);
  }

  const page = button.ownerDocument;
  // Whether space went down to speak, so that its release always lets go,
  // wherever the caret has moved and whatever is held by then.
  let spaceHeld = false;
  const onKeyDown = (event: KeyboardEvent): void => {
    if (!spaceRecords(button, event, onKeyDown, onKeyUp)) {
      return;
    }

    event.preventDefault();

    if (!event.repeat) {
      spaceHeld = true;
      begin();
    }
  };
  const onKeyUp = (event: KeyboardEvent): void => {
    if (event.key !== " " || !spaceHeld) {
      return;
    }

    spaceHeld = false;
    event.preventDefault();
    release();
  };

  page.addEventListener("keydown", onKeyDown);
  page.addEventListener("keyup", onKeyUp);
  button.addEventListener("contextmenu", (event) => {
    event.preventDefault();
  });

  button.hidden = false;
  status?.removeAttribute("hidden");
  noteSpace(button, status);
}

/**
 * Say under the button that space records too.
 *
 * Drawn here rather than in either page's markup, so it is only ever said
 * where this is listening for the key. The stylesheet shows it only where the
 * pointer is a mouse, which is where a keyboard is most likely to be.
 */
function noteSpace(
  button: HTMLButtonElement,
  status: HTMLElement | null,
): void {
  const note = button.ownerDocument.createElement("p");

  note.className = "cb-fuxi-speak-keys";
  tellOn(note, speechSaid.space);
  (status ?? button).after(note);
}
